import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { loginUser } from "@/services/auth";
import { useAppDispatch } from "@/modules/app/useAppContext";
import { setLoggedinUser } from "@/modules/app/appSlice";
import { authenticateDemo } from "@/helpers/apis";
import { RelativeComponentLoader } from "@/components/loader";
import { toast } from "@altimateai/lego";

/**
 * Component that handles demo session authentication from URL parameters
 * Must be rendered inside BrowserRouter context
 */
export const DemoTokenHandler = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const demoSessionId = searchParams.get("demoSessionId");

    if (demoSessionId && !isAuthenticating && !hasChecked) {
      setIsAuthenticating(true);

      // Authenticate the demo session with backend
      authenticateDemo(demoSessionId)
        .then(user => {
          if (!user) {
            throw new Error("No user data received from authentication");
          }
          // Login with the actual user data from backend
          loginUser(user);
          dispatch(setLoggedinUser(user));

          // Navigate to playground (clean URL without query params)
          navigate("/studio", { replace: true });
        })
        .catch(error => {
          console.error("Failed to authenticate demo session:", error);

          // Parse error message
          let errorDescription = "An unexpected error occurred";
          if (error?.response?.data?.detail) {
            errorDescription = error.response.data.detail;
          } else if (error?.response?.data?.message) {
            errorDescription = error.response.data.message;
          } else if (error?.message) {
            errorDescription = error.message;
          } else if (error) {
            errorDescription = String(error);
          }
          // Show error toast
          toast({
            variant: "destructive",
            title: "Authentication unsuccessful",
            description: errorDescription,
          });

          // On error, redirect to login
          navigate("/login", { replace: true });
        })
        .finally(() => {
          setIsAuthenticating(false);
          setHasChecked(true);
        });
    } else if (!demoSessionId) {
      setHasChecked(true);
    }
  }, [location.search, navigate, dispatch, isAuthenticating, hasChecked, searchParams]);

  // Show loading screen while authenticating with demo session
  if (
    isAuthenticating ||
    (!hasChecked && new URLSearchParams(location.search).has("demoSessionId"))
  ) {
    return <RelativeComponentLoader label="Authenticating demo session..." />;
  }

  return <>{children}</>;
};
